import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { LoginRequest, SignupRequest, TotpVerifyRequest } from "@mailflare/shared";

import { queryKeys } from "@/lib/query-keys";

import { getSession, listPasskeys, listSessions, login, logout, signup, verifyTotp } from "./api";

export function useSession() {
  return useQuery({
    queryKey: queryKeys.session,
    queryFn: getSession,
    retry: false,
    staleTime: 60_000,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: LoginRequest) => login(body),
    onSuccess: (result) => {
      if (result.status === "authenticated") {
        queryClient.setQueryData(queryKeys.session, result.session);
      }
    },
  });
}

export function useSignup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: SignupRequest) => signup(body),
    onSuccess: (session) => {
      queryClient.setQueryData(queryKeys.session, session);
    },
  });
}

export function useVerifyTotp() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: TotpVerifyRequest) => verifyTotp(body),
    onSuccess: (session) => {
      queryClient.setQueryData(queryKeys.session, session);
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: logout,
    onSuccess: () => {
      queryClient.clear();
    },
  });
}

export function usePasskeys() {
  return useQuery({ queryKey: queryKeys.passkeys, queryFn: listPasskeys });
}

export function useSessions() {
  return useQuery({ queryKey: queryKeys.sessions, queryFn: listSessions });
}
